/**
 * Shared navigation registry — single source of truth for both the desktop
 * sidebar (Layout.jsx) and the mobile drawer / bottom tab bar (MobileTabBar.jsx).
 *
 * 2026-02-09 · Extracted after a user reported that sales_manager on
 * iPhone Safari saw an empty menu because MobileTabBar had its own
 * out-of-date role list.
 */
import {
  Package, FileText, Contact, Factory, ClipboardList,
  Store, Tag, Trash2, LifeBuoy, Image as ImageIcon, ImageOff, PackageOpen,
  FileSpreadsheet, Users, Settings, ShieldCheck,
} from "lucide-react";

// Roles that can see the general "staff" set (products page + can create quotes etc).
const STAFF = ["admin", "export", "gt_export", "buyer", "procurement_manager"];

/**
 * Build the list of primary navigation items visible to a role.
 * Each item: { to, label, icon, testId }.
 */
export function buildNavItems(role, t) {
  const isStaff = STAFF.includes(role);
  const isProductionManager = role === "production_manager";
  const isSalesManager = role === "sales_manager";
  const isMarketSurveyor = role === "market_surveyor";
  const isProductManager = role === "product_manager";
  const isFactory = role === "factory";
  const isCustomer = role === "customer";
  // 2026-06 · user_admin sees ONLY 用户管理 — nothing else.
  const isUserAdmin = role === "user_admin";
  if (isUserAdmin) {
    return [{ to: "/users", label: t("nav.users") || "用户管理", icon: Users, testId: "nav-users" }];
  }

  return [
    // Products — visible to everyone who can see the catalogue
    (isStaff || isFactory || isCustomer || isProductionManager || isSalesManager || isMarketSurveyor || isProductManager)
      ? { to: "/", label: t("nav.products"), icon: Package, testId: "nav-products" } : null,

    // Quotes — admin/export/customer + sales_manager. 2026-06 · 采购 /
    // 采购经理 also get a READ-ONLY view of the quotes containing their products.
    !isProductionManager && !isProductManager
    && (["admin", "export", "customer", "buyer", "procurement_manager"].includes(role) || isSalesManager)
      ? { to: "/quotes", label: t("nav.quotes"), icon: FileText, testId: "nav-quotes" } : null,

    // Customers — admin/export + sales_manager
    !isProductionManager && !isProductManager && (["admin", "export"].includes(role) || isSalesManager)
      ? { to: "/customers", label: t("nav.customers"), icon: Contact, testId: "nav-customers" } : null,

    // Factories — admin/buyer/procurement_manager
    !isProductionManager && !isSalesManager && !isProductManager && ["admin", "buyer", "procurement_manager"].includes(role)
      ? { to: "/factories", label: t("nav.factories"), icon: Factory, testId: "nav-factories" } : null,

    // Procurement quotes — staff (incl. sales_manager who can see the whole
    // team's records per 2026-02-09 spec). Excludes production_manager and
    // product_manager (product_manager's scope is limited per 2026-02-27 spec).
    !isProductionManager && !isProductManager && (isStaff || isSalesManager)
      ? { to: "/procurement-quotes", label: t("nav.procurementQuotes"), icon: ClipboardList, testId: "nav-procurement-quotes" } : null,

    // 2026-09-30 · 采购核价模版 — admin / 采购经理可编辑；采购 / 外销 / 销售主管只读
    !isProductionManager && !isProductManager && !isMarketSurveyor && (isStaff || isSalesManager)
      ? { to: "/pq-templates", label: "核价模版", icon: FileSpreadsheet, testId: "nav-pq-templates" } : null,

    // 2026-02-27 · 产品一览表 — the primary module for product_manager. Also
    // available to staff / sales_manager. 2026-06 · 产品经理
    // (production_manager) gets read-only visibility per user spec.
    (isStaff || isSalesManager || isProductManager || isProductionManager)
      ? { to: "/product-overview", label: t("nav.productOverview") || "产品一览表", icon: ImageIcon, testId: "nav-product-overview" } : null,

    // Market survey — every logged-in role
    { to: "/market-survey", label: t("nav.marketSurvey") || "市场调查表", icon: Store, testId: "nav-market-survey" },

    // Tags — product_manager gets READ-ONLY access (2026-06 user spec).
    { to: "/tags", label: t("nav.tags"), icon: Tag, testId: "nav-tags" },

    // 2026-06 · 寄样管理 — all staff
    { to: "/sample-lists", label: t("sample.manage"), icon: PackageOpen, testId: "nav-sample-lists" },

    // Recycle bin — admin/export
    !isProductionManager && !isSalesManager && !isProductManager && ["admin", "export"].includes(role)
      ? { to: "/trash", label: t("nav.trash"), icon: Trash2, testId: "nav-trash" } : null,

    // 2026-06 · 坏图清单 — admin-only maintenance tool
    role === "admin"
      ? { to: "/bad-images", label: t("nav.badImages"), icon: ImageOff, testId: "nav-bad-images" } : null,

    // Help — every logged-in role
    { to: "/help", label: t("nav.help"), icon: LifeBuoy, testId: "nav-help" },
  ].filter(Boolean);
}

/**
 * Admin-only items that live in the desktop header (top-right). On mobile
 * they should be available inside the drawer so admins aren't cut off.
 */
export function buildAdminItems(role, t) {
  if (role !== "admin") return [];
  return [
    { to: "/system-templates", label: t("nav.systemTemplates"), icon: FileSpreadsheet, testId: "nav-system-templates" },
    { to: "/users",            label: t("nav.users"),           icon: Users,            testId: "nav-users" },
    { to: "/backup-health",    label: "备份与健康",              icon: ShieldCheck,      testId: "nav-backup-health" },
    { to: "/settings",         label: t("nav.settings"),        icon: Settings,         testId: "nav-settings" },
  ];
}
